/**
 * Полоска «Ждут ответа» панели расширения и «Чатов» — ровно то, что отдаёт `GET /api/ext/waiting`.
 * Спека `docs/specs/2026-10-09-waiting-bar-design.md`.
 */
import { apiFetch } from "@/lib/api";
import type { Channel, Chat } from "@/lib/clientChats";

export type WaitingRow = {
  id: number;
  channel: Channel;
  /** Где отвечать: `app` — из «Чатов» (официальный вход канала), `bridge` — из «Чатов» через мост,
   * `web` — только в мессенджере, в браузере с этим аккаунтом. */
  place: "app" | "bridge" | "web";
  name: string;
  phone: string | null;
  username: string;
  /** Наш аккаунт чата — как в списке «Чатов», без отметки «кто онлайн». */
  account: Pick<Chat["account"], "web" | "name" | "phone">;
  /** Чат нашего номера: id аккаунта и собеседника на площадке — сверить с вкладкой, открыть диалог. */
  account_id: string | null;
  peer_id: string | null;
  since: string;
  minutes: number;
  /** Аккаунт чата сейчас открыт в браузере смотрящего. */
  mine: boolean;
  /** У кого ещё он открыт сейчас (самая свежая отметка). */
  owner: { id: number; name: string } | null;
};

export type Waiting = {
  threshold: number;
  alert: number;
  /** `here` + `browser`: чаты «у коллег» в число не входят. */
  count: number;
  /** Минут у самого давнего из них; 0 — никого. */
  oldest: number;
  level: "none" | "warn" | "alert";
  /** Право `client_chats`: без него блока «здесь» нет. */
  can_here: boolean;
  here_online: { active: boolean; users: { id: number; name: string }[] };
  here: WaitingRow[];
  browser: WaitingRow[];
  colleagues: WaitingRow[];
  /** Вторая полоска — номера, ждущие перезвона после пропущенного входящего (те же, что сверху
   * «Обзвона»); общая очередь КЦ. Без права `client_chats` — `null`. */
  callback: Callback | null;
  /** Третья — лиды, ждущие дозвона: «не распределено» и «повторные прозвоны» «Распределения»;
   * без права `client_chats` — `null`. */
  leads: Leads | null;
};

export type Leads = { new: number; redial: number };

export type CallbackRow = {
  phone: string;
  /** Последний пропуск эпизода и первый (с него и `minutes`), сколько ждал секунд, линия словами. */
  at: string;
  from: string;
  count: number;
  wait: number;
  line: string;
  /** Наших исходящих после пропуска; с 3 номер уходит из очереди. */
  attempts: number;
  minutes: number;
};

export type Callback = {
  /** Норма перезвона «Звонков», минут: дольше — красным. */
  norm: number;
  count: number;
  oldest: number;
  level: "none" | "warn" | "alert";
  rows: CallbackRow[];
};

export const WAITING_POLL = 30_000;
export const loadWaiting = () => apiFetch<Waiting>("/api/ext/waiting");

/** «27 мин», «1 ч 05 мин», «2 дн» — сколько ждёт (как в сводке «Клиенты ждут ответа»). */
export function waited(min: number): string {
  if (min < 60) return `${min} мин`;
  if (min < 24 * 60) return `${Math.floor(min / 60)} ч ${String(min % 60).padStart(2, "0")} мин`;
  return `${Math.floor(min / (24 * 60))} дн`;
}
